import {
  Bot,
  TrendingUp,
  LayoutDashboard,
  Code2,
  ArrowRight,
  Workflow,
  Database,
  GitBranch,
} from "lucide-react";

const PILLARS = [
  {
    id: "automation",
    icon: Bot,
    title: "Automatización",
    tagline: "// adios.tareas_repetitivas",
    desc: "Eliminamos tareas repetitivas con lógica personalizada. Tu equipo se enfoca en lo que importa.",
    bullets: [
      "Workflows con n8n / Zapier",
      "Bots con IA (GPT-5)",
      "Integraciones API a la medida",
    ],
    accent: "orange",
    aux: Workflow,
  },
  {
    id: "scalability",
    icon: TrendingUp,
    title: "Escalabilidad",
    tagline: "// crece.sin_limites",
    desc: "Sistemas que crecen al ritmo de tu empresa. Arquitectura cloud lista para 10x tu tráfico.",
    bullets: [
      "FastAPI + MongoDB / Postgres",
      "Deploy en Kubernetes",
      "CI/CD automatizado",
    ],
    accent: "sky",
    aux: Database,
  },
  {
    id: "control",
    icon: LayoutDashboard,
    title: "Control Total",
    tagline: "// decisiones.en_tiempo_real",
    desc: "Paneles de administración para tomar decisiones con datos. Sin intermediarios, sin Excel.",
    bullets: [
      "Dashboards interactivos",
      "Reportes y exportaciones",
      "Roles y permisos finos",
    ],
    accent: "orange",
    aux: GitBranch,
  },
];

import { Rich } from "@/components/Rich";
import { withDefaults } from "@/lib/sections";

export default function SoftwareServices({ content }) {
  const c = withDefaults("software", content);
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="software"
      data-testid="section-software"
      className="relative py-20 sm:py-28 border-t border-slate-900 overflow-hidden"
    >
      {/* Decorative background — subtle, doesn't compete with Services section */}
      <div
        className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-orange-500/5 blur-3xl pointer-events-none"
        aria-hidden
      />
      <div
        className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-sky-500/5 blur-3xl pointer-events-none"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/5 text-orange-400 font-mono text-xs">
              <Code2 size={14} />
              <span>{c.eyebrow}</span>
            </div>
            <h2
              data-testid="software-title"
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white"
            >
              <Rich text={c.title} accent="text-slate-500 font-mono" />
            </h2>
            <p
              data-testid="software-subtitle"
              className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl"
            >
              <Rich text={c.subtitle} />
            </p>
          </div>

          <div className="lg:col-span-4 lg:text-right">
            <div className="inline-block rounded-xl border border-slate-800 bg-slate-900/60 p-4 font-mono text-xs">
              <div className="text-slate-500"># stack.preferido</div>
              <div className="mt-1 text-sky-400">
                FastAPI · React · MongoDB
              </div>
              <div className="text-orange-400">+ IA · n8n · Stripe</div>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            const Aux = p.aux;
            const isOrange = p.accent === "orange";
            return (
              <article
                key={p.id}
                data-testid={`software-card-${p.id}`}
                className={`group relative rounded-2xl border bg-slate-900/60 p-7 transition-all duration-300 hover:-translate-y-1 ${
                  isOrange
                    ? "border-slate-800 hover:border-orange-500/40"
                    : "border-slate-800 hover:border-sky-500/40"
                }`}
              >
                {/* Aux deco icon */}
                <Aux
                  size={120}
                  className={`absolute -right-6 -bottom-6 opacity-[0.04] pointer-events-none ${
                    isOrange ? "text-orange-400" : "text-sky-400"
                  }`}
                  aria-hidden
                />

                <div
                  className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl border ${
                    isOrange
                      ? "bg-orange-500/10 border-orange-500/30 text-orange-400"
                      : "bg-sky-500/10 border-sky-500/30 text-sky-400"
                  }`}
                >
                  <Icon size={22} />
                </div>

                <h3 className="relative mt-5 text-xl font-semibold text-white">
                  {p.title}
                </h3>
                <p
                  className={`relative font-mono text-xs mt-1 ${
                    isOrange ? "text-orange-400" : "text-sky-400"
                  }`}
                >
                  {p.tagline}
                </p>
                <p className="relative mt-3 text-slate-400 text-sm leading-relaxed">
                  {p.desc}
                </p>

                <ul className="relative mt-6 space-y-2.5 font-mono text-xs">
                  {p.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-slate-300"
                    >
                      <span
                        className={`mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                          isOrange ? "bg-orange-500" : "bg-sky-500"
                        }`}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* CTA strip */}
        <div
          data-testid="software-cta-strip"
          className="mt-14 rounded-2xl border border-orange-500/30 bg-gradient-to-br from-slate-900 via-slate-900 to-orange-950/20 p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div className="max-w-xl">
            <div className="font-mono text-xs text-orange-400">
              // ¿tienes una idea?
            </div>
            <h3 className="mt-2 text-2xl sm:text-3xl font-semibold text-white">
              Convirtamos tu proceso en código.
            </h3>
            <p className="mt-2 text-slate-400 text-sm sm:text-base">
              MVPs en 4-6 semanas. Sistemas internos en 8-12. Cuéntanos qué
              quieres automatizar y te entregamos un plan claro.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 shrink-0">
            <button
              onClick={() => goTo("cotizador")}
              data-testid="software-cta-quote"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-all shadow-[0_0_25px_rgba(249,115,22,0.35)] hover:shadow-[0_0_35px_rgba(249,115,22,0.55)]"
            >
              Cotizar Proyecto de Software
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <button
              onClick={() => goTo("agendar")}
              data-testid="software-cta-book"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-sky-400 hover:text-sky-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-sky-500/30 transition-all"
            >
              Agendar consultoría
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
